// src/components/ui/Toast.jsx
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useToast } from '../../context/ToastContext';

const ToastContainer = () => {
  const { toasts, removeToast } = useToast();

  return createPortal(
    <div className="fixed top-4 right-4 left-4 md:left-auto p-2 z-[9999] w-auto md:max-w-sm pointer-events-none">
      <div className="space-y-3">
        {toasts.map(toast => (
          <ToastItem
            key={toast.id}
            toast={toast}
            onClose={() => removeToast(toast.id)}
          />
        ))}
      </div>
    </div>,
    document.body
  );
};

const ToastItem = ({ toast, onClose }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);

  // Animate entrance
  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 10);
    return () => clearTimeout(timer);
  }, []);

  const handleClose = () => {
    setIsLeaving(true);
    setTimeout(() => onClose(), 300);
  };

  // Get the appropriate accent color based on toast type
  const getAccentColor = () => {
    switch(toast.type) {
      case 'success':
        return 'border-l-green-400 shadow-green-500/20';
      case 'error':
        return 'border-l-red-400 shadow-red-500/20';
      case 'warning':
        return 'border-l-amber-400 shadow-amber-500/20';
      case 'info':
        return 'border-l-blue-400 shadow-blue-500/20';
      default:
        return 'border-l-slate-400 shadow-slate-500/20';
    }
  };

  const getIcon = () => {
    const iconClass = "w-5 h-5 drop-shadow-sm";

    switch(toast.type) {
      case 'success':
        return (
          <svg className={`${iconClass} text-green-500`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
          </svg>
        );
      case 'error':
        return (
          <svg className={`${iconClass} text-red-500`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" />
          </svg>
        );
      case 'warning':
        return (
          <svg className={`${iconClass} text-amber-500`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        );
      case 'info':
        return (
          <svg className={`${iconClass} text-blue-500`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      default:
        return (
          <svg className={`${iconClass} text-slate-500`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
    }
  };

  return (
    <div
      className={`
        pointer-events-auto
        transform transition-all duration-500 ease-out
        ${isVisible && !isLeaving
        ? 'translate-x-0 opacity-100 scale-100'
        : 'translate-x-full opacity-0 scale-95'
      }
        relative overflow-hidden
        backdrop-blur-2xl bg-white/95 dark:bg-white/90
        border border-white/40 dark:border-white/30
        border-l-4 ${getAccentColor()}
        rounded-2xl shadow-2xl ${getAccentColor().split(' ')[1]}
        hover:bg-white/98 dark:hover:bg-white/95
        hover:scale-[1.02] hover:shadow-3xl
        transition-all duration-300 ease-in-out
        min-w-[280px] sm:min-w-[320px] max-w-[calc(100vw-2rem)] sm:max-w-[400px] mx-auto
      `}
    >
      {/* Liquid gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-white/60 via-white/20 to-white/40 dark:from-white/40 dark:via-white/10 dark:to-white/20 pointer-events-none" />

      {/* Animated liquid blob */}
      <div className="absolute -top-10 -right-10 w-20 h-20 bg-gradient-to-br from-white/30 to-white/10 rounded-full blur-xl animate-pulse pointer-events-none" />
      <div className="absolute -bottom-8 -left-8 w-16 h-16 bg-gradient-to-tr from-white/20 to-transparent rounded-full blur-lg animate-pulse pointer-events-none" style={{animationDelay: '1s'}} />

      {/* Content */}
      <div className="relative flex items-start p-3 sm:p-4">
        <div className="flex-shrink-0 mt-0.5">
          {getIcon()}
        </div>

        <div className="ml-2 sm:ml-3 flex-1 min-w-0">
          <div className="text-xs sm:text-sm font-medium text-gray-800 dark:text-gray-700 leading-5 drop-shadow-sm">
            {toast.message}
          </div>
        </div>

        <button
          onClick={handleClose}
          className="
            ml-2 sm:ml-3 flex-shrink-0 mt-0.5
            p-1 sm:p-1.5 rounded-xl
            text-gray-500 dark:text-gray-600
            hover:text-gray-700 dark:hover:text-gray-800
            hover:bg-white/60 dark:hover:bg-white/40
            focus:outline-none focus:ring-2 focus:ring-white/50
            transition-all duration-200
            group backdrop-blur-sm
          "
          aria-label="Close notification"
        >
          <svg
            className="w-3 h-3 sm:w-4 sm:h-4 transition-transform duration-150 group-hover:scale-110"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Subtle bottom border glow */}
      <div className={`absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-current to-transparent opacity-20 ${
        toast.type === 'success' ? 'text-green-400' :
          toast.type === 'error' ? 'text-red-400' :
            toast.type === 'warning' ? 'text-amber-400' :
              toast.type === 'info' ? 'text-blue-400' :
                'text-slate-400'
      }`} />
    </div>
  );
};

export default ToastContainer;